import React, { useState, useEffect, useCallback } from "react";
import {
  Loader2, Store, Paintbrush, Sparkles, CheckCircle, AlertTriangle, Calendar,
  UserCheck, Clock, Filter, RefreshCw,
} from "lucide-react";
import VendorAssignmentModal from "./VendorAssignmentModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OPTIONAL_CATEGORIES = [
  "Cake & Bakery", "Photography", "DJ & Music", "Catering", "Lighting",
  "Props & Rentals", "Anchor / Host", "Magician / Entertainer", "Other",
];
const VENDOR_STATUSES = [
  "pending", "awaiting_ops_review", "partially_assigned",
  "fully_assigned", "no_vendor_available",
];

const STATUS_CLS = {
  fully_assigned: "bg-emerald-50 text-emerald-700 border-emerald-200",
  partially_assigned: "bg-amber-50 text-amber-700 border-amber-200",
  awaiting_ops_review: "bg-blue-50 text-blue-700 border-blue-200",
  no_vendor_available: "bg-red-50 text-red-700 border-red-200",
  pending: "bg-gray-100 text-gray-600 border-gray-200",
};
const prettyStatus = (s) => (s || "pending").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const Card = ({ label, value, icon: Icon, tint, testid }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-3.5" data-testid={testid}>
    <Icon size={14} style={{ color: tint }} className="mb-1" />
    <p className="text-lg font-bold text-[#1E1B4B]">{value ?? 0}</p>
    <p className="text-[10px] text-[#64748B] leading-tight font-medium">{label}</p>
  </div>
);

const ReadyDot = ({ assigned }) => (
  assigned ? <CheckCircle size={13} className="text-emerald-500" /> : <span className="inline-block w-2.5 h-2.5 rounded-full bg-gray-300" />
);

const VendorSchedulerPanel = ({ authAxios }) => {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openBooking, setOpenBooking] = useState(null);
  const [filters, setFilters] = useState({
    booking_date: "", event_date: "", pincode: "", ops_member: "",
    vendor_status: "", pending_mandatory: "", optional_service: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const [s, b] = await Promise.all([
        authAxios.get(`${API_URL}/api/scheduler/vendors/summary`),
        authAxios.get(`${API_URL}/api/scheduler/vendors/board`, { params }),
      ]);
      setSummary(s.data);
      setRows(b.data.rows || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [authAxios, filters]);

  useEffect(() => { load(); }, [load]);

  const opsMembers = Array.from(new Set(rows.map((r) => r.assigned_ops_name).filter(Boolean)));

  return (
    <div className="space-y-4" data-testid="vendor-scheduler-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-2"><Store size={15} className="text-[#7C3AED]" /> Vendor Assignment</h3>
        <button onClick={load} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 text-[10px] font-bold text-[#475569] hover:bg-gray-50" data-testid="vendor-refresh">
          <RefreshCw size={11} /> Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5" data-testid="vendor-summary-cards">
        <Card testid="card-total-confirmed" label="Total Confirmed" value={summary?.total_confirmed} icon={Calendar} tint="#1E1B4B" />
        <Card testid="card-ops-assigned" label="OPS Assigned" value={summary?.ops_assigned} icon={UserCheck} tint="#3B82F6" />
        <Card testid="card-vendor-pending" label="Vendor Pending" value={summary?.vendor_pending} icon={Clock} tint="#F97316" />
        <Card testid="card-partially" label="Partially Assigned" value={summary?.partially_assigned} icon={Sparkles} tint="#F59E0B" />
        <Card testid="card-fully" label="Fully Assigned" value={summary?.fully_assigned} icon={CheckCircle} tint="#10B981" />
        <Card testid="card-upcoming" label="Upcoming Events" value={summary?.upcoming_events} icon={Calendar} tint="#7C3AED" />
        <Card testid="card-no-vendor" label="No Mandatory Vendor" value={summary?.no_mandatory_vendor} icon={AlertTriangle} tint="#EF4444" />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-3" data-testid="vendor-filters">
        <div className="flex items-center gap-1.5 mb-2 text-[11px] font-bold text-[#64748B]"><Filter size={12} /> Filters</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          <input type="date" value={filters.booking_date} onChange={(e) => setFilters((f) => ({ ...f, booking_date: e.target.value }))} title="Booking date" className="px-2 py-1.5 rounded-lg border border-gray-200 text-[11px]" data-testid="filter-booking-date" />
          <input type="date" value={filters.event_date} onChange={(e) => setFilters((f) => ({ ...f, event_date: e.target.value }))} title="Event date" className="px-2 py-1.5 rounded-lg border border-gray-200 text-[11px]" data-testid="filter-event-date" />
          <input type="text" placeholder="Pincode" value={filters.pincode} onChange={(e) => setFilters((f) => ({ ...f, pincode: e.target.value }))} className="px-2 py-1.5 rounded-lg border border-gray-200 text-[11px]" data-testid="filter-pincode" />
          <select value={filters.ops_member} onChange={(e) => setFilters((f) => ({ ...f, ops_member: e.target.value }))} className="px-2 py-1.5 rounded-lg border border-gray-200 text-[11px]" data-testid="filter-ops-member">
            <option value="">OPS member</option>
            {opsMembers.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={filters.vendor_status} onChange={(e) => setFilters((f) => ({ ...f, vendor_status: e.target.value }))} className="px-2 py-1.5 rounded-lg border border-gray-200 text-[11px]" data-testid="filter-vendor-status">
            <option value="">Vendor status</option>
            {VENDOR_STATUSES.map((s) => <option key={s} value={s}>{prettyStatus(s)}</option>)}
          </select>
          <select value={filters.optional_service} onChange={(e) => setFilters((f) => ({ ...f, optional_service: e.target.value }))} className="px-2 py-1.5 rounded-lg border border-gray-200 text-[11px]" data-testid="filter-optional-service">
            <option value="">Optional service</option>
            {OPTIONAL_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-[11px] font-semibold text-[#475569] px-2 py-1.5 rounded-lg border border-gray-200 cursor-pointer" data-testid="filter-pending-mandatory-label">
            <input type="checkbox" checked={filters.pending_mandatory === "1"} onChange={(e) => setFilters((f) => ({ ...f, pending_mandatory: e.target.checked ? "1" : "" }))} data-testid="filter-pending-mandatory" />
            Pending mandatory
          </label>
        </div>
      </div>

      {/* Board table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-testid="vendor-board">
        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[#DC283C]" size={22} /></div>
        ) : rows.length === 0 ? (
          <p className="text-xs text-center text-[#94a3b8] py-10">No confirmed bookings match these filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-[9px] uppercase tracking-wide text-[#94a3b8] border-b border-gray-100">
                  <th className="px-3 py-2">Booking</th>
                  <th className="px-3 py-2">Event</th>
                  <th className="px-3 py-2">Pincode</th>
                  <th className="px-3 py-2">OPS</th>
                  <th className="px-3 py-2 text-center">Balloon Shop</th>
                  <th className="px-3 py-2 text-center">Decorator</th>
                  <th className="px-3 py-2">Optional</th>
                  <th className="px-3 py-2">Readiness</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50/50 text-[11px]" data-testid={`vendor-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <p className="font-bold text-[#1E1B4B] truncate max-w-[140px]">{r.customer_name}</p>
                      <p className="text-[9px] text-[#94a3b8] truncate max-w-[140px]">{r.id}</p>
                    </td>
                    <td className="px-3 py-2 text-[#475569]">{String(r.date).slice(0, 10)}<br /><span className="text-[9px] text-[#94a3b8]">{r.setup_time}</span></td>
                    <td className="px-3 py-2 font-semibold text-[#1E1B4B]">{r.pincode || "—"}</td>
                    <td className="px-3 py-2 text-[#475569]">{r.assigned_ops_name || <span className="text-amber-600 font-semibold">—</span>}</td>
                    <td className="px-3 py-2 text-center" title={r.balloon_shop.name}><div className="flex justify-center"><ReadyDot assigned={r.balloon_shop.assigned} /></div></td>
                    <td className="px-3 py-2 text-center" title={r.decorator.name}><div className="flex justify-center"><ReadyDot assigned={r.decorator.assigned} /></div></td>
                    <td className="px-3 py-2 text-[#475569]">
                      {r.optional_total > 0 ? `${r.optional_assigned_count}/${r.optional_total}` : <span className="text-[#cbd5e1]">none</span>}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold border ${STATUS_CLS[r.vendor_assignment_status] || STATUS_CLS.pending}`}>{prettyStatus(r.vendor_assignment_status)}</span>
                    </td>
                    <td className="px-3 py-2">
                      <button onClick={() => setOpenBooking(r.id)} data-testid={`manage-vendors-${r.id}`}
                        className="px-2.5 py-1 rounded-lg bg-[#7C3AED] text-white text-[10px] font-bold hover:bg-[#6D28D9]">Manage</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openBooking && (
        <VendorAssignmentModal bookingId={openBooking} authAxios={authAxios}
          onClose={() => setOpenBooking(null)} onChanged={load} />
      )}
    </div>
  );
};

export default VendorSchedulerPanel;
